import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Button, Container, Form } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import { fi } from 'date-fns/locale';
import { Icon } from '@lomarengas/design-system';
import qs from 'qs';
import { format } from 'date-fns';
import {
  addStatusToast,
  DataTable,
  DataTableItem,
  Loader,
  PaginationComponent,
  SearchBar,
  SearchFilterDropdown,
  SectionHeader,
} from '../../common/components';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  AccountExtendedListItem,
  AccountsFilter,
  AccountUpdateData,
  addUserPrivateLoginAsync,
  deleteUserLoginAsync,
  getAllAccountsFromDbAsync,
  getAllRolesAsync,
  markAccountDeletedAsyncAsync,
  restoreUserLoginAsync,
  selectAllAccountsFromDb,
  selectAllAccountsLoadingStatus,
  selectAllAccountsPagination,
  selectCaretakerRoles,
  selectOfficerRoles,
  selectOwnerRole,
  selectTravelAgencyRole,
} from './identityUsersSlice';
import { LOADING_STATUS, LOCALES } from '../../common/constants';
import {
  InitialPaginationParameters,
  PopulatePagination,
} from '../../common/types/pagination.types';
import { dateToDateOnlyString } from '../../common/utils/dateUtils';

function IdentityUsers() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialRoleFilter = searchParams.get('roleFilter') || 'owners';
  const dispatch = useAppDispatch();

  const [hasSearchBeenMade, setHasSearchBeenMade] = useState(false);
  const [searchTerm, setSearchTerm] = useState(null);
  const [activeRoleFilter, setActiveRoleFilter] = useState<string | null>(null);

  const accounts = useAppSelector(selectAllAccountsFromDb);
  const accountsLoadingStatus = useAppSelector(selectAllAccountsLoadingStatus);
  const accountsPagination = useAppSelector(selectAllAccountsPagination);
  const officerRoles = useAppSelector(selectOfficerRoles);
  const caretakerRoles = useAppSelector(selectCaretakerRoles);
  const ownerRole = useAppSelector(selectOwnerRole);
  const travelAgencyRole = useAppSelector(selectTravelAgencyRole);

  const [accountsSearchFilter, setAccountsSearchFilter] =
    useState<AccountsFilter>({
      PageNumber: InitialPaginationParameters.PageNumber,
      PageSize: InitialPaginationParameters.PageSize,
      searchString: searchTerm,
      culture: LOCALES.Fi,
      roleIds: [],
      isActive: true,
      paysVat: undefined,
      hasPersonalIdentityCode: undefined,
    });
  const [accountsTableItems, setAccountsTableItems] = useState<DataTableItem[]>(
    [],
  );
  const [extraAccountProperties, setExtraAccountProperties] = useState({});

  const handleInputChange = event => {
    const { target } = event;
    const { value } = target;
    setSearchTerm(value);
  };

  const getAccountIdNumber = (account: AccountExtendedListItem) => {
    const roleFilter = activeRoleFilter || initialRoleFilter;
    switch (roleFilter) {
      case 'caretakers':
        return account.caretakerNumber;
        break;
      case 'owners':
        return account.ownerNumber;
        break;
      case 'officers':
        return account.officerNumber;
        break;
      case 'travelAgencies':
        return account.travelAgencyNumber;
        break;
      default:
        return '-';
    }
  };

  useEffect(() => {
    setActiveRoleFilter(initialRoleFilter);
  }, [initialRoleFilter]);

  useEffect(() => {
    if (accounts && accounts.length > 0) {
      const formattedAccountsList: DataTableItem[] = [];
      const tempExtraAccountProperties = {};

      accounts?.forEach(item => {
        const queryParams = qs.stringify(
          {
            ...accountsSearchFilter,
            roleIds: accountsSearchFilter.roleIds,
            roleFilter: initialRoleFilter,
          },
          { arrayFormat: 'repeat' },
        );

        formattedAccountsList.push({
          ItemId: item.id,
          Items: [
            getAccountIdNumber(item),
            `${item.firstName} ${item.lastName}`,
            item.companyName,
            item.contactPersonName,
            item.postCity,
            item.userName,
            item.phoneNumber,
            item.hasUserLogin ? '✔️' : '',
            item.hasPersonalIdentityCode ? '✔️' : '',
          ],
          Url: `/users/details/${item.id}?${queryParams}`,
        });

        tempExtraAccountProperties[item.id] = {
          userName: item.userName,
          hasUserLogin: item.hasUserLogin,
          isDeleted: item.isDeleted,
          hasPersonalIdentityCode: item.hasPersonalIdentityCode,
        };
      });
      setAccountsTableItems(formattedAccountsList);
      setExtraAccountProperties(tempExtraAccountProperties);
    } else {
      setAccountsTableItems([]);
    }
  }, [accounts]);

  useEffect(() => {
    if (accountsTableItems && accountsTableItems.length > 0) {
      setHasSearchBeenMade(true);
    }
  }, [accountsTableItems]);

  const navigate = useNavigate();

  const doSearch = async event => {
    if (event !== null) {
      event.preventDefault();
    }

    let searchFilterRoles = [];
    switch (activeRoleFilter) {
      case 'owners':
        searchFilterRoles =
          ownerRole !== null ? ownerRole.map(role => role.id) : [];
        break;
      case 'caretakers':
        searchFilterRoles =
          caretakerRoles !== null ? caretakerRoles.map(role => role.id) : [];
        break;
      case 'officers':
        searchFilterRoles =
          officerRoles !== null ? officerRoles.map(role => role.id) : [];
        break;
      case 'travelAgencies':
        searchFilterRoles =
          travelAgencyRole !== null
            ? travelAgencyRole.map(role => role.id)
            : [];
        break;
      case 'allAccounts':
        searchFilterRoles = travelAgencyRole !== null ? [''] : [''];
        break;
      default:
        searchFilterRoles = [];
        break;
    }

    const modifiedSearchFilter = {
      ...accountsSearchFilter,
      PageNumber: InitialPaginationParameters.PageNumber,
      searchString: searchTerm,
      roleIds: searchFilterRoles,
    };

    setAccountsSearchFilter(modifiedSearchFilter);

    // Update URL query parameters
    searchParams.set('roleFilter', activeRoleFilter);
    searchParams.set('isActive', String(accountsSearchFilter.isActive));
    if (accountsSearchFilter.createdFrom) {
      searchParams.set(
        'createdFrom',
        format(accountsSearchFilter.createdFrom, 'yyyy-MM-dd'),
      );
    }
    if (accountsSearchFilter.createdTo) {
      searchParams.set(
        'createdTo',
        format(accountsSearchFilter.createdTo, 'yyyy-MM-dd'),
      );
    }
    if (accountsSearchFilter.updatedFrom) {
      searchParams.set(
        'updatedFrom',
        format(accountsSearchFilter.updatedFrom, 'yyyy-MM-dd'),
      );
    }
    if (accountsSearchFilter.updatedTo) {
      searchParams.set(
        'updatedTo',
        format(accountsSearchFilter.updatedTo, 'yyyy-MM-dd'),
      );
    }
    if (accountsSearchFilter.companyId) {
      searchParams.set('companyId', accountsSearchFilter.companyId);
    }
    if (accountsSearchFilter.paysVat !== null) {
      searchParams.set('paysVat', String(accountsSearchFilter.paysVat));
    }
    if (accountsSearchFilter.hasPersonalIdentityCode !== null) {
      searchParams.set(
        'hasPersonalIdentityCode',
        String(accountsSearchFilter.hasPersonalIdentityCode),
      );
    }
    if (modifiedSearchFilter.searchString) {
      searchParams.set('searchString', modifiedSearchFilter.searchString);
    } else {
      searchParams.delete('searchString');
    }
    if (modifiedSearchFilter.roleIds) {
      searchParams.delete('roleIds');
      modifiedSearchFilter.roleIds.forEach(roleId => {
        searchParams.append('roleIds', roleId);
      });
    }

    setSearchParams(searchParams);
    setSearchTerm(modifiedSearchFilter.searchString);
    const resultAction = await dispatch(
      getAllAccountsFromDbAsync(modifiedSearchFilter),
    );
    if (getAllAccountsFromDbAsync.fulfilled.match(resultAction)) {
      const { data } = resultAction.payload;
      const pagination = PopulatePagination(resultAction.payload);
      if (pagination.totalCount === 1 && data?.length === 1) {
        const queryParams = qs.stringify(
          { ...modifiedSearchFilter, roleFilter: activeRoleFilter },
          { arrayFormat: 'repeat' },
        );
        navigate(`/users/details/${data[0].id}?${queryParams}`);
      }
    }
  };
  useEffect(() => {
    const isActive = searchParams.get('isActive');
    const createdFrom = searchParams.get('createdFrom');
    const createdTo = searchParams.get('createdTo');
    const updatedFrom = searchParams.get('updatedFrom');
    const updatedTo = searchParams.get('updatedTo');
    const companyId = searchParams.get('companyId');
    const paysVat = searchParams.get('paysVat');
    const hasPersonalIdentityCode = searchParams.get('hasPersonalIdentityCode');
    const searchString = searchParams.get('searchString');
    const roleIds = searchParams.getAll('roleIds');
    const doSearchParam = searchParams.getAll('doSearchParam');

    setAccountsSearchFilter(prevState => ({
      ...prevState,
      isActive: isActive ? isActive === 'true' : true,
      createdFrom: createdFrom ? new Date(createdFrom) : null,
      createdTo: createdTo ? new Date(createdTo) : null,
      updatedFrom: updatedFrom ? new Date(updatedFrom) : null,
      updatedTo: updatedTo ? new Date(updatedTo) : null,
      companyId: companyId || null,
      paysVat: paysVat ? paysVat === 'true' : null,
      hasPersonalIdentityCode: hasPersonalIdentityCode
        ? hasPersonalIdentityCode === 'true'
        : null,
      searchString: searchString || '',
      roleIds,
    }));
    if (doSearchParam && doSearchParam.length > 0) {
      const currentfilter = {
        isActive: isActive ? isActive === 'true' : true,
        createdFrom: createdFrom ? new Date(createdFrom) : null,
        createdTo: createdTo ? new Date(createdTo) : null,
        updatedFrom: updatedFrom ? new Date(updatedFrom) : null,
        updatedTo: updatedTo ? new Date(updatedTo) : null,
        companyId: companyId || null,
        paysVat: paysVat ? paysVat === 'true' : null,
        hasPersonalIdentityCode: hasPersonalIdentityCode
          ? hasPersonalIdentityCode === 'true'
          : null,
        searchString: searchString || '',
        roleIds: ownerRole ? ownerRole.map(role => role.id) : [],
        culture: LOCALES.Fi,
        PageNumber: 1,
      } as AccountsFilter;
      dispatch(getAllAccountsFromDbAsync(currentfilter));
      setHasSearchBeenMade(true);
    }
    setActiveRoleFilter(initialRoleFilter);
    setSearchTerm(searchString);
  }, [searchParams]);
  const initFetch = useCallback(() => {
    dispatch(
      getAllRolesAsync({
        pageNumber: 1,
        pageSize: 10,
        culture: LOCALES.Fi,
        searchString: '',
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    initFetch();
  }, [initFetch]);

  const handlePaginationClick = (Page: number) => {
    const paginatedFiltering = accountsSearchFilter;
    paginatedFiltering.PageNumber = Page;
    setAccountsSearchFilter(paginatedFiltering);
    // dispatch(getAllAccountsFromDbAsync(paginatedFiltering));
    // if (event !== null) {
    //   event.preventDefault();
    // }

    let searchFilterRoles = [];
    switch (activeRoleFilter) {
      case 'owners':
        searchFilterRoles =
          ownerRole !== null ? ownerRole.map(role => role.id) : [];
        break;
      case 'caretakers':
        searchFilterRoles =
          caretakerRoles !== null ? caretakerRoles.map(role => role.id) : [];
        break;
      case 'officers':
        searchFilterRoles =
          officerRoles !== null ? officerRoles.map(role => role.id) : [];
        break;
      case 'travelAgencies':
        searchFilterRoles =
          travelAgencyRole !== null
            ? travelAgencyRole.map(role => role.id)
            : [];
        break;
      case 'allAccounts':
        searchFilterRoles = travelAgencyRole !== null ? [''] : [''];
        break;
      default:
        searchFilterRoles = [];
        break;
    }

    const modifiedSearchFilter = {
      ...accountsSearchFilter,
      PageNumber: paginatedFiltering.PageNumber,
      searchString: searchTerm,
      roleIds: searchFilterRoles,
    };

    setAccountsSearchFilter(modifiedSearchFilter);

    // Update URL query parameters
    searchParams.set('roleFilter', activeRoleFilter);
    searchParams.set('isActive', String(accountsSearchFilter.isActive));
    if (accountsSearchFilter.createdFrom) {
      searchParams.set(
        'createdFrom',
        format(accountsSearchFilter.createdFrom, 'yyyy-MM-dd'),
      );
    }
    if (accountsSearchFilter.createdTo) {
      searchParams.set(
        'createdTo',
        format(accountsSearchFilter.createdTo, 'yyyy-MM-dd'),
      );
    }
    if (accountsSearchFilter.updatedFrom) {
      searchParams.set(
        'updatedFrom',
        format(accountsSearchFilter.updatedFrom, 'yyyy-MM-dd'),
      );
    }
    if (accountsSearchFilter.updatedTo) {
      searchParams.set(
        'updatedTo',
        format(accountsSearchFilter.updatedTo, 'yyyy-MM-dd'),
      );
    }
    if (accountsSearchFilter.companyId) {
      searchParams.set('companyId', accountsSearchFilter.companyId);
    }
    if (accountsSearchFilter.paysVat !== null) {
      searchParams.set('paysVat', String(accountsSearchFilter.paysVat));
    }
    if (accountsSearchFilter.hasPersonalIdentityCode !== null) {
      searchParams.set(
        'hasPersonalIdentityCode',
        String(accountsSearchFilter.hasPersonalIdentityCode),
      );
    }
    if (modifiedSearchFilter.searchString) {
      searchParams.set('searchString', modifiedSearchFilter.searchString);
    } else {
      searchParams.delete('searchString');
    }
    if (modifiedSearchFilter.roleIds) {
      searchParams.delete('roleIds');
      modifiedSearchFilter.roleIds.forEach(roleId => {
        searchParams.append('roleIds', roleId);
      });
    }

    setSearchParams(searchParams);
    setSearchTerm(modifiedSearchFilter.searchString);
    dispatch(getAllAccountsFromDbAsync(modifiedSearchFilter));
    setHasSearchBeenMade(true);
  };

  const handleCreateUserAccountLogin = async (
    AccountId: string,
    Email: string,
  ) => {
    const resultAction = await dispatch(
      addUserPrivateLoginAsync({ accountId: AccountId }),
    );

    if (addUserPrivateLoginAsync.fulfilled.match(resultAction)) {
      dispatch(
        addStatusToast({
          Title: 'Kirjautumistunnusten luonti',
          Message: `Käyttäjän sähköpostiosoitteelle ${Email} on luotu kirjautumistunnukset.`,
        }),
      );
    } else if (addUserPrivateLoginAsync.rejected.match(resultAction)) {
      dispatch(
        addStatusToast({
          IsFailed: true,
          Title: 'Kirjautumistunnusten luonti epäonnistui',
          Message: `Kirjautumistunnusten luonti käyttäjälle epäonnistui.`,
        }),
      );
    }
  };

  const handleDeleteUserAccountLogin = async (
    AccountId: string,
    Email: string,
  ) => {
    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(
      'Haluatko varmasti poistaa kirjautumistunnuksen käyttäjältä?',
    );
    if (!confirmed) {
      return;
    }

    const resultAction = await dispatch(
      deleteUserLoginAsync({ accountId: AccountId }),
    );

    if (deleteUserLoginAsync.fulfilled.match(resultAction)) {
      dispatch(
        addStatusToast({
          Title: 'Kirjautumistunnusten poisto',
          Message: `Käyttäjän sähköpostiosoitteelle ${Email} luodut kirjautumistunnukset on poistettu.`,
        }),
      );
    } else if (deleteUserLoginAsync.rejected.match(resultAction)) {
      dispatch(
        addStatusToast({
          IsFailed: true,
          Title: 'Kirjautumistunnusten poisto epäonnistui',
          Message: `Käyttäjän kirjautumistunnusten poistaminen epäonnistui.`,
        }),
      );
    }
  };
  const handleRestoreUserAccountLogin = async (
    AccountId: string,
    Email: string,
  ) => {
    // eslint-disable-next-line no-alert
    const confirmed = window.confirm('Haluatko varmasti palauttaa käyttäjän?');
    if (!confirmed) {
      return;
    }
    const resultAction = await dispatch(
      restoreUserLoginAsync({ accountId: AccountId }),
    );

    if (restoreUserLoginAsync.fulfilled.match(resultAction)) {
      dispatch(
        addStatusToast({
          Title: 'Kirjautumistunnusten poisto',
          Message: `Käyttäjän sähköpostiosoitteelle ${Email} luodut kirjautumistunnukset on poistettu.`,
        }),
      );
    } else if (restoreUserLoginAsync.rejected.match(resultAction)) {
      dispatch(
        addStatusToast({
          IsFailed: true,
          Title: 'Kirjautumistunnusten poisto epäonnistui',
          Message: `Käyttäjän kirjautumistunnusten poistaminen epäonnistui.`,
        }),
      );
    }
  };

  // TODO: This functionality should be moved to details component, now this was accidentally clicked multiple times
  const handleDeleteUser = (AccountId: string) => {
    // eslint-disable-next-line no-alert
    const confirmed = window.confirm('Haluatko varmasti poistaa käyttäjän?');
    if (!confirmed) {
      return;
    }

    const accountUpdateData = {
      id: AccountId,
      contactUpdateData: null,
      accountsRoles: null,
    } as AccountUpdateData;
    dispatch(markAccountDeletedAsyncAsync(accountUpdateData)).then(res => {
      if (res.meta.requestStatus === 'fulfilled') {
        dispatch(
          addStatusToast({
            Message: `Käyttäjä on poistettu.`,
          }),
        );
        dispatch(getAllAccountsFromDbAsync(accountsSearchFilter));
        setHasSearchBeenMade(true);
      } else if (res.meta.requestStatus === 'rejected') {
        dispatch(
          addStatusToast({
            IsFailed: true,
            Message: `Käyttäjän poistamisessa tapahtui virhe.`,
          }),
        );
      }
    });
  };

  const AddNewButton: JSX.Element = (
    <Link to="/users/addnew" className="btn btn-blue ps-16">
      <Icon CssClass="me-8" AriaLabel="Uusi" Name="plus" />
      Lisää uusi
    </Link>
  );

  const getExtraPropertyById = (id, propertyKey) => {
    if (extraAccountProperties[id]) {
      return extraAccountProperties[id][propertyKey];
    }
    return '';
  };

  const handleRoleFilterChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { id } = event.target;
    setActiveRoleFilter(id);

    // Clean search criterias when role filter changes
    setAccountsSearchFilter(prevState => ({
      ...prevState,
      PageNumber: InitialPaginationParameters.PageNumber,
      PageSize: InitialPaginationParameters.PageSize,
      searchString: searchTerm,
      culture: LOCALES.Fi,
    }));
  };

  const handleSearchFilterChange = (field: string, value: unknown) => {
    const formattedValue =
      value instanceof Date ? format(value, 'yyyy-MM-dd') : value;

    setAccountsSearchFilter(prevState => ({
      ...prevState,
      [field]: formattedValue,
    }));
  };

  const handleResetFilters = () => {
    setAccountsSearchFilter({
      PageNumber: InitialPaginationParameters.PageNumber,
      PageSize: InitialPaginationParameters.PageSize,
      searchString: '',
      culture: LOCALES.Fi,
      roleIds: [],
      isActive: true,
    });
    setSearchTerm('');
    setSearchParams({});
    setAccountsTableItems([]);
  };

  const handleRemoveFilter = (filterKey: string) => {
    setAccountsSearchFilter(prevState => ({
      ...prevState,
      [filterKey]: null,
    }));
  };

  const formatSelectedValue = (label, value, filterKey: string) => {
    if (
      value === null ||
      value === undefined ||
      value === 0 ||
      value === '0' ||
      value === ''
    ) {
      return null;
    }

    let displayValue = value;
    if (typeof value === 'boolean') {
      displayValue = value ? 'Kyllä' : 'Ei';
    }

    return (
      <span className="bg-light rounded-pill small py-1 px-16 d-flex align-items-center gap-8">
        {label}: {displayValue}
        <Button
          variant="link"
          className="p-0"
          onClick={() => handleRemoveFilter(filterKey)}
        >
          <span className="bi bi-x" />
        </Button>
      </span>
    );
  };

  const formatSelectedDates = (
    label: string,
    value: Date,
    value2: Date,
    filterKey: string,
    filterKey2: string,
  ) => {
    if (
      value === null ||
      value2 === null ||
      value === undefined ||
      value2 === undefined ||
      value < new Date('2020-01-01') ||
      value2 < new Date('2020-01-01')
    ) {
      return null;
    }
    return (
      <span className="bg-light rounded-pill small py-1 px-16 d-flex align-items-center gap-8">
        {label}: {dateToDateOnlyString(value)} - {dateToDateOnlyString(value2)}
        <Button
          variant="link"
          className="p-0"
          onClick={() => {
            handleRemoveFilter(filterKey);
            handleRemoveFilter(filterKey2);
          }}
        >
          <span className="bi bi-x" />
        </Button>
      </span>
    );
  };

  return (
    <>
      <Helmet>
        <title>Käyttäjät - Haku</title>
      </Helmet>

      <Container className="container-content">
        <SectionHeader
          Breadcrumbs={['Käyttäjät']}
          Children={AddNewButton}
          Title="Käyttäjät"
        />

        <SearchBar
          FreeTextOnChangeHandler={handleInputChange}
          FreeTextPlaceholder={searchTerm ?? 'Hae...'}
          SearchHandler={doSearch}
          IsLoading={accountsLoadingStatus !== LOADING_STATUS.IDLE}
          ResetHandler={handleResetFilters}
          ShowResetButton
          Value={searchTerm}
        >
          {accountsLoadingStatus === LOADING_STATUS.IDLE && (
            <div className="d-flex align-items-center p-24 pt-0">
              <div className="flex-grow-1 me-24">
                <Form.Check
                  inline
                  label="Omistajat"
                  name="role"
                  type="radio"
                  id="owners"
                  onChange={handleRoleFilterChange}
                  checked={activeRoleFilter === 'owners'}
                />
                <Form.Check
                  inline
                  label="Huoltajat"
                  name="role"
                  type="radio"
                  id="caretakers"
                  onChange={handleRoleFilterChange}
                  checked={activeRoleFilter === 'caretakers'}
                />
                <Form.Check
                  inline
                  label="Virkailijat"
                  name="role"
                  type="radio"
                  id="officers"
                  onChange={handleRoleFilterChange}
                  checked={activeRoleFilter === 'officers'}
                />
                <Form.Check
                  inline
                  label="Matkanjärjestäjät"
                  name="role"
                  type="radio"
                  id="travelAgencies"
                  onChange={handleRoleFilterChange}
                  checked={activeRoleFilter === 'travelAgencies'}
                />
                <Form.Check
                  inline
                  label="Kaikki käyttäjät"
                  name="role"
                  type="radio"
                  id="allAccounts"
                  onChange={handleRoleFilterChange}
                  checked={activeRoleFilter === 'allAccounts'}
                />
              </div>
            </div>
          )}
          {accountsLoadingStatus === LOADING_STATUS.IDLE && (
            <div className="position-relative px-24 pt-0 pb-8">
              <SearchFilterDropdown
                ShowCloseButton={false}
                DefaultValue={false}
                Id="users-filter-active"
                Label="Aktiivisuus"
                Value={accountsSearchFilter.isActive ?? false}
              >
                <Form.Group>
                  <Form.Check
                    type="switch"
                    id="custom-switch"
                    label="Aktiivinen"
                    checked={accountsSearchFilter.isActive}
                    onChange={e =>
                      handleSearchFilterChange('isActive', e.target.checked)
                    }
                  />
                </Form.Group>
              </SearchFilterDropdown>
              <SearchFilterDropdown
                ShowCloseButton={false}
                DefaultValue={false}
                Id="users-filter-created"
                Label="Luotu"
                Value={
                  !!accountsSearchFilter.createdFrom ||
                  !!accountsSearchFilter.createdTo
                }
              >
                <Form.Group className="mb-3">
                  <Form.Label>Alkaen</Form.Label>
                  <DatePicker
                    locale={fi}
                    selected={
                      accountsSearchFilter.createdFrom
                        ? new Date(accountsSearchFilter.createdFrom)
                        : null
                    }
                    dateFormat="dd.MM.yyyy"
                    onChange={date =>
                      handleSearchFilterChange('createdFrom', date)
                    }
                    showWeekNumbers
                    className="form-control"
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Päättyen</Form.Label>
                  <DatePicker
                    locale={fi}
                    selected={
                      accountsSearchFilter.createdTo
                        ? new Date(accountsSearchFilter.createdTo)
                        : null
                    }
                    minDate={
                      accountsSearchFilter.createdFrom
                        ? new Date(accountsSearchFilter.createdFrom)
                        : null
                    }
                    dateFormat="dd.MM.yyyy"
                    onChange={date =>
                      handleSearchFilterChange('createdTo', date)
                    }
                    showWeekNumbers
                    className="form-control"
                    disabled={!accountsSearchFilter.createdFrom}
                  />
                </Form.Group>
              </SearchFilterDropdown>
              <SearchFilterDropdown
                ShowCloseButton={false}
                DefaultValue={false}
                Id="users-filter-modified"
                Label="Muokattu"
                Value={
                  !!accountsSearchFilter.updatedFrom ||
                  !!accountsSearchFilter.updatedTo
                }
              >
                <Form.Group className="mb-3">
                  <Form.Label>Alkaen</Form.Label>
                  <DatePicker
                    locale={fi}
                    selected={
                      accountsSearchFilter.updatedFrom
                        ? new Date(accountsSearchFilter.updatedFrom)
                        : null
                    }
                    dateFormat="dd.MM.yyyy"
                    onChange={date =>
                      handleSearchFilterChange('updatedFrom', date)
                    }
                    showWeekNumbers
                    className="form-control"
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Päättyen</Form.Label>
                  <DatePicker
                    locale={fi}
                    selected={
                      accountsSearchFilter.updatedTo
                        ? new Date(accountsSearchFilter.updatedTo)
                        : null
                    }
                    minDate={
                      accountsSearchFilter.updatedFrom
                        ? new Date(accountsSearchFilter.updatedFrom)
                        : null
                    }
                    dateFormat="dd.MM.yyyy"
                    onChange={date =>
                      handleSearchFilterChange('updatedTo', date)
                    }
                    showWeekNumbers
                    className="form-control"
                    disabled={!accountsSearchFilter.updatedFrom}
                  />
                </Form.Group>
              </SearchFilterDropdown>
              {(activeRoleFilter === 'owners' ||
                activeRoleFilter === 'caretakers') && (
                <SearchFilterDropdown
                  ShowCloseButton={false}
                  DefaultValue=""
                  Id="users-filter-ycode"
                  Label="Y-tunnus"
                  Value={accountsSearchFilter.companyId || ''}
                >
                  <Form.Group className="mb-3">
                    <Form.Label>Y-tunnus</Form.Label>
                    <Form.Control
                      type="text"
                      value={accountsSearchFilter.companyId || ''}
                      onChange={e =>
                        handleSearchFilterChange('companyId', e.target.value)
                      }
                    />
                  </Form.Group>
                </SearchFilterDropdown>
              )}
              {(activeRoleFilter === 'owners' ||
                activeRoleFilter === 'caretakers') && (
                <>
                  <SearchFilterDropdown
                    ShowCloseButton={false}
                    DefaultValue={undefined}
                    Id="users-filter-alv"
                    Label="ALV"
                    Value={accountsSearchFilter.paysVat ?? undefined}
                  >
                    <Form.Group>
                      <Form.Check
                        type="switch"
                        id="custom-switch"
                        label="ALV velvollisuus"
                        checked={accountsSearchFilter.paysVat ?? false}
                        onChange={e =>
                          handleSearchFilterChange('paysVat', e.target.checked)
                        }
                      />
                    </Form.Group>
                  </SearchFilterDropdown>
                  <SearchFilterDropdown
                    ShowCloseButton={false}
                    DefaultValue={undefined}
                    Id="users-filter-pic"
                    Label="Henkilötunnus"
                    Value={
                      accountsSearchFilter.hasPersonalIdentityCode ?? undefined
                    }
                  >
                    <Form.Group>
                      <Form.Check
                        type="switch"
                        id="custom-switch-pic"
                        label="Henkilötunnus"
                        checked={
                          accountsSearchFilter.hasPersonalIdentityCode ?? false
                        }
                        onChange={e =>
                          handleSearchFilterChange(
                            'hasPersonalIdentityCode',
                            e.target.checked,
                          )
                        }
                      />
                    </Form.Group>
                  </SearchFilterDropdown>
                </>
              )}
              {/*
              {activeRoleFilter === 'owners' && (
                <SearchFilterDropdown
                  ShowCloseButton={false}
                  DefaultValue={null}
                  Id="users-filter-group-owners"
                  Label="Omistajaryhmä"
                  Value={null}
                >
                  <Form.Group className="mb-3">
                    <Form.Select aria-label="Default select example">
                      <option>Open this select menu</option>
                      <option value="1">One</option>
                      <option value="2">Two</option>
                      <option value="3">Three</option>
                    </Form.Select>
                  </Form.Group>
                </SearchFilterDropdown>
              )}
              */}
              {/*
              {activeRoleFilter === 'caretakers' && (
                <SearchFilterDropdown
                  ShowCloseButton={false}
                  DefaultValue={null}
                  Id="users-filter-group-caretakers"
                  Label="Huoltajaryhmä"
                  Value={null}
                >
                  <Form.Group className="mb-3">
                    <Form.Select aria-label="Default select example">
                      <option>Open this select menu</option>
                      <option value="1">One</option>
                      <option value="2">Two</option>
                      <option value="3">Three</option>
                    </Form.Select>
                  </Form.Group>
                </SearchFilterDropdown>
              )}
              */}
              {activeRoleFilter === 'travelAgencies' && (
                <>
                  <SearchFilterDropdown
                    ShowCloseButton={false}
                    DefaultValue={null}
                    Id="users-filter-country"
                    Label="Maa"
                    Value={null}
                  >
                    <Form.Group>
                      <Form.Select aria-label="Default select example">
                        <option>Open this select menu</option>
                        <option value="1">One</option>
                        <option value="2">Two</option>
                        <option value="3">Three</option>
                      </Form.Select>
                    </Form.Group>
                  </SearchFilterDropdown>
                  {/*
                  <SearchFilterDropdown
                    ShowCloseButton={false}
                    DefaultValue={null}
                    Id="users-filter-orders"
                    Label="Varausten määrä"
                    Value={null}
                  >
                    <Form.Group>
                      <Form.Control type="text" />
                    </Form.Group>
                  </SearchFilterDropdown>
                  <SearchFilterDropdown
                    ShowCloseButton={false}
                    DefaultValue={null}
                    Id="users-filter-orders-dates"
                    Label="Varaukset aikavälillä"
                    Value={null}
                  >
                    <Form.Group className="mb-3">
                      <Form.Label>Alkaen</Form.Label>
                      <DatePicker locale={fi}
                        selected={startDate}
                        onChange={date => setStartDate(date)}
                        showWeekNumbers
                        className="form-control"
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Päättyen</Form.Label>
                      <DatePicker locale={fi}
                        selected={startDate}
                        onChange={date => setStartDate(date)}
                        showWeekNumbers
                        className="form-control"
                      />
                    </Form.Group>
                  </SearchFilterDropdown>
                  */}
                </>
              )}
              <div className="d-flex align-items-center p-24">
                <div className="d-flex flex-wrap align-items-center gap-8">
                  {formatSelectedDates(
                    'Luotu',
                    accountsSearchFilter.createdFrom,
                    accountsSearchFilter.createdTo,
                    'createdFrom',
                    'createdTo',
                  )}
                  {formatSelectedDates(
                    'Muokattu',
                    accountsSearchFilter.updatedFrom,
                    accountsSearchFilter.updatedTo,
                    'updatedFrom',
                    'updatedTo',
                  )}
                  {formatSelectedValue(
                    'Y-tunnus',
                    accountsSearchFilter.companyId || '',
                    'companyId',
                  )}
                  {formatSelectedValue(
                    'ALV',
                    accountsSearchFilter.paysVat,
                    'paysVat',
                  )}
                  {formatSelectedValue(
                    'Henkilötunnus',
                    accountsSearchFilter.hasPersonalIdentityCode,
                    'hasPersonalIdentityCode',
                  )}
                </div>
              </div>
            </div>
          )}
        </SearchBar>

        <Loader IsLoading={accountsLoadingStatus === LOADING_STATUS.LOADING} />

        {accountsLoadingStatus === LOADING_STATUS.IDLE &&
          accountsTableItems !== null &&
          accountsTableItems.length > 0 &&
          hasSearchBeenMade && (
            <>
              <DataTable
                ColumnWidths={[120, 0, 0, 0, 0, 0, 120, 120, 80]}
                Header={[
                  'Numero',
                  'Nimi',
                  'Yrityksen nimi',
                  'Yhteyshenkilö',
                  'Postitoimipaikka',
                  'Sähköposti',
                  'Puhelinnumero',
                  'Tunnukset',
                  'HETU',
                ]}
                HideDeleteButton
                ItemActions={id => {
                  const userHasAccount = getExtraPropertyById(
                    id,
                    'hasUserLogin',
                  );
                  const IsDeletedAccount = getExtraPropertyById(
                    id,
                    'isDeleted',
                  );

                  return [
                    {
                      ButtonAction: () => {
                        if (userHasAccount) {
                          handleDeleteUserAccountLogin(
                            id,
                            getExtraPropertyById(id, 'userName'),
                          );
                        } else {
                          handleCreateUserAccountLogin(
                            id,
                            getExtraPropertyById(id, 'userName'),
                          );
                        }
                      },
                      ButtonText: userHasAccount
                        ? 'Poista tunnukset'
                        : 'Luo tunnukset',
                    },
                    {
                      ButtonAction: () => {
                        if (IsDeletedAccount) {
                          handleRestoreUserAccountLogin(
                            id,
                            getExtraPropertyById(id, 'userName'),
                          );
                        }
                      },
                      ButtonText: IsDeletedAccount ? 'Palauta käyttäjä' : '',
                    },
                  ];
                }}
                Items={accountsTableItems}
                LegendText="käyttäjää"
                TotalItems={accountsPagination.totalCount}
              />
              <PaginationComponent
                OnPageClick={handlePaginationClick}
                PaginationData={accountsPagination}
              />
            </>
          )}

        {hasSearchBeenMade &&
          accountsLoadingStatus === LOADING_STATUS.IDLE &&
          accounts.length < 1 && <p>Ei käyttäjiä annetuilla hakuehdoilla.</p>}
      </Container>
    </>
  );
}
export default IdentityUsers;
